/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React from 'react';
import { Col, Form, Row } from 'reactstrap';
import {
  GROUND_MOTION_METHOD,
  GROUND_MOTION_UPLOAD,
  HAZARD_EDITION,
  INCLUDE_DESIGN_INTENSITIES,
  RETURN_PERIODS,
  RETURN_PERIOD_DE,
  RETURN_PERIOD_MCE,
  SITE_CLASS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { Field, FormGroup, Fieldset } from '#components/pages/Models/Model/support/form/index.js';
import GroundMotionTable from './GroundMotionTable/index.js';
import GroundMotionUploader from './GroundMotionUploader/index.js';
import SiteClassRange from './SiteClassRange/index.js';

type Props = {|
  fieldsByName: Object,
  form: string,
  heading: string,
  modelId: string,
  onBack: Function,
  onNext: Function,
  country: string,
  dbe: string,
  mce: string,
  groundMotionClosestVs30: string,
  showDesignIntensities: boolean,
  showReturnPeriodInputs: boolean,
  showGroundMotionTable: boolean,
  returnPeriodOptions: Array<{| value: number, text: number |}>,
|};

const GroundMotionSection = (props: Props): Element<'div'> => {
  const {
    fieldsByName,
    heading,
    form,
    modelId,
    onBack,
    onNext,
    country,
    dbe,
    mce,
    groundMotionClosestVs30,
    showDesignIntensities,
    showReturnPeriodInputs,
    showGroundMotionTable,
    returnPeriodOptions,
  } = props;

  const showIntTooltip = !['JP', 'US'].includes(country);
  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <Fieldset form={form} fields={[SITE_CLASS]}>
          <legend>Soil Information</legend>
          <Row>
            <Col lg="6">
              <Field field={fieldsByName[SITE_CLASS]} />
            </Col>
          </Row>
          <SiteClassRange form={form} />
        </Fieldset>
        <FormGroup tag="fieldset">
          <legend>
            Ground Motion Hazard Info{' '}
            <Tip tipId={showIntTooltip ? 'groundMotionInt' : 'groundMotion'} />
          </legend>
          <FormGroup>
            <Row>
              <Col lg="6">
                <Field field={fieldsByName[GROUND_MOTION_METHOD]} />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup form={form} fields={[HAZARD_EDITION]}>
            <Row>
              <Col lg="6">
                <Field field={fieldsByName[HAZARD_EDITION]} />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup form={form} fields={[INCLUDE_DESIGN_INTENSITIES]}>
            <Field field={fieldsByName[INCLUDE_DESIGN_INTENSITIES]} />
          </FormGroup>
          <FormGroup form={form} fields={[RETURN_PERIODS]}>
            <FormGroup tag="fieldset">
              <legend>
                Return Periods <Tip tipId={RETURN_PERIODS} />
              </legend>
              {showDesignIntensities && (
                <div id="designIntensities" className="pt-2 pb-3">
                  <div>
                    DE :<span className="pl-1 pr-1">{dbe}</span>
                    <Tip tipId={RETURN_PERIOD_DE} />
                  </div>
                  <div>
                    MCE :<span className="pl-1 pr-1">{mce}</span>
                    <Tip tipId={RETURN_PERIOD_MCE} />
                  </div>
                </div>
              )}
              <Row>
                <Col lg="9">
                  <Field field={fieldsByName[RETURN_PERIODS]} />
                </Col>
              </Row>
            </FormGroup>
          </FormGroup>
          <FormGroup form={form} fields={[GROUND_MOTION_UPLOAD]}>
            <GroundMotionUploader
              form={form}
              name={GROUND_MOTION_UPLOAD}
              modelId={modelId}
              accept=".csv"
              maxBytes={3e6}
            />
          </FormGroup>
          {showReturnPeriodInputs && (
            <div id="designIntensityInputs">
              <FormGroup tag="fieldset" form={form} fields={[RETURN_PERIOD_DE, RETURN_PERIOD_MCE]}>
                <legend>
                  Return Periods
                  <Tip tipId="returnPeriodsForGroundMotionUpload" />
                </legend>
                <Row>
                  <Col lg="9">
                    <Field
                      field={{ ...fieldsByName[RETURN_PERIOD_DE], options: returnPeriodOptions }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg="9">
                    <Field
                      field={{ ...fieldsByName[RETURN_PERIOD_MCE], options: returnPeriodOptions }}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </div>
          )}
          {showGroundMotionTable && (
            <div className="pt-2 pb-2">
              <GroundMotionTable modelId={modelId} />
              {groundMotionClosestVs30 && (
                <div>Closest VS30 for USGS Hazard Lookup (m/s): {groundMotionClosestVs30}</div>
              )}
            </div>
          )}
        </FormGroup>
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default GroundMotionSection;
